import { BACKEND } from '../config';

/**
 * Сторож версии сборки: вкладка сама перезагружается, когда сервер выкатили заново.
 *
 * Зачем (капсула docs/tasks/2026-08-18-client-version-autoreload.md). 18.08.2026 представитель
 * не смог скачать отмены: его вкладка неделями крутила сборку до `6cd0662d` (07.08), где
 * запрос за файлом уходил без `credentials: 'include'`, а тем же коммитом бэкенд перевёл
 * ссылку скачивания на свой прокси под авторизацией. Старый клиент + новый сервер = запрос без
 * сессии → редирект на логин → 405, и пользователь видел «Не удалось скачать ни одного файла».
 * Класс шире одного скачивания: любой разрыв контракта клиент↔сервер молча ломает
 * долгоживущую вкладку.
 *
 * ЭТАЛОН — ВЕРСИЯ НА МОМЕНТ ЗАГРУЗКИ, А НЕ «ВЕРСИЯ ФРОНТА == ВЕРСИЯ БЭКА». Сравнение с
 * запечённой в сборку строкой требует обвязки Docker ARG → .env → CRA; промах в ней делает
 * условие «не равны» вечно истинным, то есть отправляет вкладку в бесконечную перезагрузку.
 * Здесь такой петли нет по построению: после перезагрузки эталон берётся заново из того же
 * источника, и он заведомо совпадёт.
 *
 * Перенос в Nova: да — правка не-доменная (учтётся sync_debt).
 */

type WindowLike = Pick<
  Window,
  | 'addEventListener'
  | 'removeEventListener'
  | 'setTimeout'
  | 'clearTimeout'
  | 'setInterval'
  | 'clearInterval'
>;

type DocumentLike = Pick<Document, 'addEventListener' | 'removeEventListener'> & {
  visibilityState: DocumentVisibilityState;
};

export type AppVersionWatchOptions = {
  /** Как часто спрашивать версию. По умолчанию 5 минут. */
  intervalMs?: number;
  /** Простой пользователя, после которого перезагрузка считается безопасной. По умолчанию 60 с. */
  idleMs?: number;
  /** Потолок ожидания: дальше перезагружаем, даже если пользователь всё ещё работает. */
  maxWaitMs?: number;
  /** Вызывается один раз, когда обнаружена новая версия (например, показать тост). */
  onUpdateDetected?: () => void;
  /** Точки подмены для тестов. */
  fetchImpl?: typeof fetch;
  reload?: () => void;
  windowImpl?: WindowLike;
  documentImpl?: DocumentLike;
};

const DEFAULT_INTERVAL_MS = 5 * 60 * 1000;
const DEFAULT_IDLE_MS = 60 * 1000;
const DEFAULT_MAX_WAIT_MS = 5 * 60 * 1000;

// События, по которым считаем, что пользователь работает прямо сейчас. Список намеренно
// короткий: нужен не точный учёт активности, а ответ на вопрос «человек сейчас что-то делает
// в этой вкладке или нет».
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel'] as const;

/**
 * Запускает сторож. Возвращает функцию остановки (для размонтирования).
 *
 * Неудачный опрос намеренно не считается событием: сеть моргнула, сессия истекла, ответ пришёл
 * не JSON-ом — эталон НЕ сбрасывается и перезагрузка НЕ назначается. Иначе первый же обрыв
 * связи перезагружал бы страницу, то есть сторож сам стал бы источником потери работы.
 */
export const startAppVersionWatch = (options: AppVersionWatchOptions = {}): (() => void) => {
  const {
    intervalMs = DEFAULT_INTERVAL_MS,
    idleMs = DEFAULT_IDLE_MS,
    maxWaitMs = DEFAULT_MAX_WAIT_MS,
    onUpdateDetected,
    fetchImpl = typeof fetch === 'function' ? fetch.bind(globalThis) : undefined,
    reload = () => window.location.reload(),
    windowImpl = typeof window !== 'undefined' ? (window as WindowLike) : undefined,
    documentImpl = typeof document !== 'undefined' ? (document as DocumentLike) : undefined,
  } = options;

  if (!fetchImpl || !windowImpl || !documentImpl) return () => {};

  let baseline: string | null = null;
  let stopped = false;
  let pendingSince: number | null = null;
  let lastActivityAt = Date.now();
  let pollTimer: number | null = null;
  let waitTimer: number | null = null;

  function onActivity() {
    lastActivityAt = Date.now();
  }

  function onVisibility() {
    if (documentImpl!.visibilityState === 'hidden') {
      // Скрытая вкладка — самый безопасный момент: работать в ней сейчас некому.
      if (pendingSince !== null) doReload();
      return;
    }
    lastActivityAt = Date.now();
    void check();
  }

  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (pollTimer !== null) windowImpl.clearInterval(pollTimer);
    if (waitTimer !== null) windowImpl.clearTimeout(waitTimer);
    ACTIVITY_EVENTS.forEach((event) => windowImpl.removeEventListener(event, onActivity));
    windowImpl.removeEventListener('focus', onVisibility);
    documentImpl.removeEventListener('visibilitychange', onVisibility);
  };

  const readVersion = async (): Promise<string | null> => {
    try {
      const response = await fetchImpl(`${BACKEND}/api/app-version/`, {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
      });
      if (!response.ok) return null;
      const data = (await response.json()) as { version?: unknown };
      const version = typeof data?.version === 'string' ? data.version.trim() : '';
      return version || null;
    } catch {
      // Недоступность сервера — штатный исход опроса, а не проглоченная ошибка: сторож просто
      // ждёт следующего круга, эталон и решение о перезагрузке не трогаются.
      return null;
    }
  };

  const doReload = () => {
    if (stopped) return;
    stop();
    reload();
  };

  /** Перезагрузка на первом безопасном моменте: вкладка скрыта / человек простаивает / вышло время. */
  const tryReloadWhenSafe = () => {
    if (stopped || pendingSince === null) return;
    const waited = Date.now() - pendingSince;
    const idle = Date.now() - lastActivityAt;
    if (documentImpl.visibilityState === 'hidden' || idle >= idleMs || waited >= maxWaitMs) {
      doReload();
      return;
    }
    if (waitTimer !== null) windowImpl.clearTimeout(waitTimer);
    const nextCheckIn = Math.max(1000, Math.min(idleMs - idle, maxWaitMs - waited));
    waitTimer = windowImpl.setTimeout(tryReloadWhenSafe, nextCheckIn) as unknown as number;
  };

  const check = async () => {
    if (stopped || pendingSince !== null) return;
    const version = await readVersion();
    if (stopped || version === null) return;
    if (baseline === null) {
      baseline = version;
      return;
    }
    if (version === baseline) return;
    pendingSince = Date.now();
    onUpdateDetected?.();
    tryReloadWhenSafe();
  };

  ACTIVITY_EVENTS.forEach((event) =>
    windowImpl.addEventListener(event, onActivity, { passive: true })
  );
  windowImpl.addEventListener('focus', onVisibility);
  documentImpl.addEventListener('visibilitychange', onVisibility);
  pollTimer = windowImpl.setInterval(() => void check(), intervalMs) as unknown as number;
  void check();

  return stop;
};
